import pb from "../../lib/pocketbase";
import Logo from "../../components/icons/Logo";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Status } from "../../lib/types";
import Button from "../../components/form/button";
import { _Classes } from "../../lib/styles";
import ErrorMessage from "../../components/form/errorMessage";
import { Helmet } from "react-helmet";
import { Input } from "../../components/ui/input";
import ModeSwitch from "../../components/ModeSwitch";
import { DatePicker } from "../../components/ui/datepicker";
import AccountCreated from "../../components/studio/auth/accountCreated";
interface LoginData {
    username: string;
    email: string;
    password: string;
    passwordConfirm: string;
    birthday: Date | null;
}

export default function Registrate() {
    const [accountCreatedModal, setAccountCreatedModal] = useState(false);
    const navigate = useNavigate();

    const [data, setData] = useState<LoginData>({
        email: "",
        username: "",
        password: "",
        passwordConfirm: "",
        birthday: null
    });

    const [status, setStatus] = useState<Status>("none");
    const [error, setError] = useState<string>("");

    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    async function registrate() {
        setStatus("loading");

        if (!data.email || !emailRegex.test(data.email)) {
            setStatus("error");
            setError("Email is not valid.");
            setTimeout(() => {
                setStatus("none");
            }, 2000);

            return;
        }
        if (data.username.length < 3) {
            setStatus("error");
            setError("Username must be longer than 2 characters");
            setTimeout(() => {
                setStatus("none");
            }, 2000);

            return;
        }
        if (!data.birthday) {
            setStatus("error");
            setError("Please fill in your birthday");
            setTimeout(() => {
                setStatus("none");
            }, 2000);

            return;
        }
        if (data.password.length < 8) {
            setStatus("error");
            setError("Password must be at least 8 characters");
            setTimeout(() => {
                setStatus("none");
            }, 2000);

            return;
        }
        if (data.password !== data.passwordConfirm) {
            setStatus("error");
            setError("Passwords don't match");
            setTimeout(() => {
                setStatus("none");
            }, 2000);
            return;
        }

        await pb
            .collection("users")
            .create(data)
            .then(async (r) => {
                setStatus("success");
                await pb.collection("users").requestVerification(data.email);
                setData({
                    email: "",
                    username: "",
                    password: "",
                    passwordConfirm: "",
                    birthday: null
                });
                pb.authStore.clear();
                setAccountCreatedModal(true);
            })
            .catch((error: Error) => {
                setError(error.message);
                setStatus("error");
            })
            .finally(() => {
                setTimeout(() => {
                    setStatus("none");
                }, 2000);
            });

        pb.collection("users").authWithPassword(data.email, data.password);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        setData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter") {
            registrate();
        }
    }

    return (
        <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-2">
            <div className="absolute right-4 top-4">
                <ModeSwitch />
            </div>
            <Helmet>
                <title>ReinVB Studio — Registrate</title>
            </Helmet>
            <div className="flex w-full max-w-sm items-start">
                <Button onClick={() => navigate(-1)} variant={"ghost"} size={"ghost"} className="text-sm text-slate-400 dark:text-slate-700">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75" />
                    </svg>
                    Back
                </Button>
            </div>
            <div className="mb-4 flex w-full max-w-sm flex-col gap-8">
                <div className="flex items-center justify-center gap-4">
                    <Logo /> <span className="text-2xl font-bold">Studio.</span>
                </div>
                <div>
                    <h2 className="text-xl font-bold">Let's create an account</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-600">After creating an account you will have to ask Rein to grant access to the site.</p>
                </div>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-3">
                <ErrorMessage error={error} close={() => setError("")} />
                <div className="flex flex-col gap-1">
                    <label htmlFor="email" className="text-sm text-slate-500 dark:text-slate-600">
                        E-mailadres
                    </label>
                    <Input onKeyDown={handleKeyDown} value={data.email} id="email" name="email" onChange={handleChange} type="email" placeholder="john.doe@example.com" />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="username" className="text-sm text-slate-500 dark:text-slate-600">
                        Username
                    </label>
                    <Input onKeyDown={handleKeyDown} value={data.username} id="username" name="username" onChange={handleChange} type="text" placeholder="JohnDoe" />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="birthday" className="text-sm text-slate-500 dark:text-slate-600">
                        Birthday
                    </label>
                    <Input
                        onKeyDown={handleKeyDown}
                        value={data.birthday ? data.birthday?.toISOString().split("T")[0] : ""}
                        id="birthday"
                        name="birthday"
                        onChange={(e) => {
                            setData((prev) => ({
                                ...prev,
                                birthday: new Date(e.target.value)
                            }));
                        }}
                        type="date"
                        placeholder="JohnDoe"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="password" className="text-sm text-slate-500 dark:text-slate-600">
                        Password
                    </label>
                    <Input onKeyDown={handleKeyDown} value={data.password} id="password" name="password" onChange={handleChange} type="password" placeholder="*************" />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="passwordConfirm" className="text-sm text-slate-500 dark:text-slate-600">
                        Password confirmed
                    </label>
                    <Input onKeyDown={handleKeyDown} value={data.passwordConfirm} id="passwordConfirm" name="passwordConfirm" onChange={handleChange} type="password" placeholder="*************" />
                </div>
                <Button onClick={registrate} status={status} variant="dark" type="submit">
                    {status === "loading" && "Creating"}
                    {status === "success" && "Created"}
                    {status === "error" && "Error"}
                    {status === "none" && "Create account"}
                </Button>
                <p className="text-sm text-slate-500 dark:text-slate-600">
                    Already have an account?{" "}
                    <Link to="/studio/login" className="font-bold text-slate-600 dark:text-slate-500">
                        Login
                    </Link>
                </p>
            </div>
            <AccountCreated open={accountCreatedModal} setOpen={setAccountCreatedModal} />
        </div>
    );
}
